import React, { useState, useEffect } from "react";

const PrivacyPolicy = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const sections = [
    { id: "intro", title: "Introduction" },
    { id: "collection", title: "Data Collection" },
    { id: "integration", title: "BI Integration Security" },
    { id: "usage", title: "Data Usage" },
    { id: "rights", title: "Your Rights" },
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      <div
        className="fixed left-0 top-0 z-50 h-1 bg-blue-600 transition-all duration-150"
        style={{ width: `${scrollProgress}%` }}
      />

      <header className="w-full bg-slate-950 px-6 py-20 sm:px-12 lg:px-24">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6 inline-block rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-blue-400">Legal & Transparency</div>
          <h1 className="mb-6 text-5xl font-extrabold tracking-tight text-white md:text-7xl">
            Privacy <span className="text-blue-500">Policy.</span>
          </h1>
          <p className="max-w-2xl text-lg leading-relaxed text-slate-400 md:text-xl">Your trust is our foundation. Learn how Clearsquare protects your Domo and QuickSight data with enterprise-grade security.</p>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-7xl flex-col gap-16 px-6 py-16 sm:px-12 lg:flex-row lg:px-24">
        <aside className="flex-shrink-0 lg:w-64">
          <div className="sticky top-24 space-y-2">
            <p className="mb-4 text-xs font-bold uppercase tracking-widest text-slate-400">Table of Contents</p>
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="block rounded-lg border-l-2 border-transparent px-4 py-2 font-medium text-slate-600 transition-all hover:border-blue-600 hover:bg-slate-100 hover:text-blue-600">
                {section.title}
              </a>
            ))}
          </div>
        </aside>

        <main className="max-w-3xl flex-1">
          <section
            id="intro"
            className="mb-16 scroll-mt-24">
            <h2 className="mb-6 text-3xl font-bold">Introduction</h2>
            <p className="mb-6 text-lg leading-relaxed text-slate-600">
              Welcome to Clearsquare. We understand that your business intelligence data is highly sensitive. This policy explains how we collect, use, and safeguard your information when you use our SaaS platform to visualize Domo and QuickSight data.
            </p>
            <p className="text-lg leading-relaxed text-slate-600">By using our service, you agree to the practices described in this policy. We never sell your data to third parties, and we prioritize "Privacy by Design" in every feature we build.</p>
          </section>

          <section
            id="collection"
            className="mb-16 scroll-mt-24">
            <h3 className="mb-6 text-2xl font-bold text-slate-800">Data Collection & Sources</h3>
            <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-6">
                <h4 className="mb-2 font-bold text-blue-600">Personal Info</h4>
                <p className="text-sm font-medium leading-relaxed text-slate-500">Email, name, and billing details required for account management and security.</p>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-6">
                <h4 className="mb-2 font-bold text-blue-600">Technical Logs</h4>
                <p className="text-sm font-medium leading-relaxed text-slate-500">IP addresses and device info used to prevent unauthorized access to your dashboards.</p>
              </div>
            </div>
          </section>

          <section
            id="integration"
            className="mb-16 scroll-mt-24">
            <h3 className="mb-6 text-2xl font-bold text-slate-800">BI Integration Security</h3>
            <div className="rounded-3xl bg-blue-600 p-8 text-white shadow-2xl shadow-blue-200">
              <h4 className="mb-4 text-xl font-bold italic">How we handle your Domo/QuickSight data:</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="rounded-full bg-white/20 p-1 text-xs">✓</span>
                  <span>
                    <strong>Zero-Storage Architecture:</strong> We pull data in real-time. We do not store your raw business datasets.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="rounded-full bg-white/20 p-1 text-xs">✓</span>
                  <span>
                    <strong>Encrypted Credentials:</strong> Your API keys are encrypted using AES-256 at the application layer.
                  </span>
                </li>
              </ul>
            </div>
          </section>

          <section
            id="rights"
            className="mb-16 scroll-mt-24">
            <h3 className="mb-6 text-2xl font-bold text-slate-800">Your Rights</h3>
            <p className="mb-4 text-lg leading-relaxed text-slate-600">Regardless of your location, we provide the following rights to all users:</p>
            <ul className="list-disc space-y-3 pl-6 italic text-slate-600">
              <li>The right to request a copy of your stored personal data.</li>
              <li>The right to delete your Clearsquare account and all associated API connections.</li>
              <li>The right to opt-out of non-essential marketing communications.</li>
            </ul>
          </section>
        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
